import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Check, Megaphone, MoreHorizontal, Radio, Sparkles, Trash2, Tv } from 'lucide-react'
import { useActiveChannelId } from '../../store/index.js'
import {
  playoutApi,
  type AutoGapFillCatalog,
  type AutoGapFillJingle,
  type AutoGapFillOptions,
  type AutoGapFillPlan,
  type AutoGapFillPlannedGap,
  type AutoGapFillRequest,
  type AutoGapFillResult,
} from '../../lib/api.js'
import { broadcastDayKey } from '../../lib/broadcast-day.js'
import { recordGridChange } from '../../lib/gridUndo.js'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'
import type { GapFillTarget } from './GapFillDialog.js'

/**
 * Mode « combler tous les trous ».
 *
 * La fenêtre de remplissage manuelle compose un trou à la fois : sur une
 * journée qui en ouvre cinquante, c'est cinquante montages identiques à la
 * main. Ici l'opérateur décrit la règle une seule fois — habillages en
 * alternance, spots des campagnes actives, infoservice au-delà d'un seuil — la
 * voit appliquée à chaque trou, puis pose le tout d'un geste.
 *
 * Le plan affiché vient du serveur et non d'un calcul local : c'est le même
 * planificateur qui posera les éléments, donc la prévisualisation ne peut pas
 * annoncer autre chose que ce qui sera écrit.
 */

interface AutoGapFillDialogProps {
  gaps:   GapFillTarget[]
  /** La grille a changé : la vue se recharge, la fenêtre reste ouverte. */
  onApplied: () => void
  /** Fin du geste : message à afficher et fermeture. */
  onDone: (message: string) => void
  onClose: () => void
}

const DEFAULT_OPTIONS: AutoGapFillOptions = {
  openerProgramIds:  [],
  closerProgramIds:  [],
  spotIds:           null,
  useInfoservice:    true,
  minInfoserviceSec: 60,
  infoserviceRepeat: true,
  maxSpotsPerGap:    null,
  respectDailyCap:   true,
}

function fmtSec(value: number) {
  const sec = Math.max(0, Math.round(value))
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}m${String(s).padStart(2, '0')}s`
  return `${m}m${String(s).padStart(2, '0')}s`
}

function fmtClock(iso: string) {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

interface RotationPickerProps {
  label:    string
  hint:     string
  jingles:  AutoGapFillJingle[]
  selected: string[]
  onToggle: (programId: string) => void
}

/**
 * Sélection d'une rotation d'habillage. L'ordre affiché est celui du catalogue,
 * mais l'ordre de passage est celui des clics : le rang choisi est donc montré
 * sur chaque ligne retenue, sinon l'alternance obtenue resterait une surprise.
 */
const RotationPicker = memo(function RotationPicker({ label, hint, jingles, selected, onToggle }: RotationPickerProps) {
  return (
    <fieldset className="auto-fill__field">
      <legend className="auto-fill__field-label">{label}</legend>
      <p className="auto-fill__field-hint">{hint}</p>
      <div className="auto-fill__rotation">
        {jingles.length === 0 && <p className="auto-fill__empty">Aucun élément d’antenne disponible</p>}
        {jingles.map(jingle => {
          const rank = selected.indexOf(jingle.programId)
          return (
            <label key={jingle.programId} className="auto-fill__option" data-selected={rank >= 0 || undefined}>
              <input
                type="checkbox"
                checked={rank >= 0}
                onChange={() => onToggle(jingle.programId)}
              />
              <span className="auto-fill__option-title">{jingle.title}</span>
              <span className="auto-fill__option-meta">{fmtSec(jingle.durationSec)}</span>
              <span className="auto-fill__option-rank">{rank >= 0 ? `#${rank + 1}` : ''}</span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
})

const PlannedGapRow = memo(function PlannedGapRow({ gap }: { gap: AutoGapFillPlannedGap }) {
  return (
    <li className="auto-fill__plan-row" data-skipped={gap.skipped || undefined}>
      <span className="auto-fill__plan-time">{fmtClock(gap.startTime)}</span>
      <span className="auto-fill__plan-gap">{fmtSec(gap.gapSec)}</span>
      <span className="auto-fill__plan-steps">
        {gap.skipped
          ? <span className="auto-fill__plan-skip">Rien ne tient dans ce trou</span>
          : gap.steps.map((step, index) => (
            <span key={index} className="auto-fill__chip" data-kind={step.kind}>
              {step.kind === 'JINGLE' && <><Tv aria-hidden="true" />{step.title}</>}
              {step.kind === 'AD_BREAK' && <><Megaphone aria-hidden="true" />{step.spots.map(spot => spot.title).join(' + ')}</>}
              {step.kind === 'INFOSERVICE' && (
                <><Radio aria-hidden="true" />{step.title}{step.passages > 1 ? ` ×${step.passages}` : ''}</>
              )}
              {step.kind === 'FREE' && <><MoreHorizontal aria-hidden="true" />bouche-trou {fmtSec(step.durationSec)}</>}
            </span>
          ))}
      </span>
      <span className="auto-fill__plan-rest">
        {gap.remainingSec > 0 ? `reste ${fmtSec(gap.remainingSec)}` : <Check aria-hidden="true" />}
      </span>
    </li>
  )
})

export const AutoGapFillDialog = memo(function AutoGapFillDialog({ gaps, onApplied, onDone, onClose }: AutoGapFillDialogProps) {
  const channelId = useActiveChannelId()
  const [catalog, setCatalog] = useState<AutoGapFillCatalog | null>(null)
  const [options, setOptions] = useState<AutoGapFillOptions>(DEFAULT_OPTIONS)
  const [plan, setPlan] = useState<AutoGapFillPlan | null>(null)
  const [loading, setLoading] = useState(true)
  const [planning, setPlanning] = useState(false)
  const [saving, setSaving] = useState(false)
  const [applied, setApplied] = useState<AutoGapFillResult | null>(null)
  const [reverting, setReverting] = useState(false)
  const [error, setError] = useState('')

  const date = useMemo(() => broadcastDayKey(gaps[0]?.gapStart ?? new Date().toISOString()), [gaps])
  const requestGaps = useMemo(() => gaps
    .filter(gap => gap.gapEnd)
    .map(gap => ({ gridId: gap.gridId, startTime: gap.gapStart, endTime: gap.gapEnd as string })),
  [gaps])

  useEffect(() => {
    if (!channelId) return
    let cancelled = false
    setLoading(true)
    playoutApi.getAutoGapFillCatalog(channelId, date)
      .then(result => { if (!cancelled) setCatalog(result) })
      .catch(() => { if (!cancelled) setError('Impossible de charger le catalogue du jour') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [channelId, date])

  // Le plan se recalcule à chaque réglage. Un court délai évite d'envoyer une
  // requête par frappe sur le seuil d'infoservice.
  const planSeq = useRef(0)
  useEffect(() => {
    if (!channelId || !catalog || requestGaps.length === 0) return
    const seq = ++planSeq.current
    setPlanning(true)
    const timer = setTimeout(() => {
      playoutApi.previewAutoGapFill(channelId, { date, gaps: requestGaps, options } satisfies AutoGapFillRequest)
        .then(result => { if (seq === planSeq.current) { setPlan(result); setError('') } })
        .catch(() => { if (seq === planSeq.current) setError('Impossible de calculer le plan') })
        .finally(() => { if (seq === planSeq.current) setPlanning(false) })
    }, 250)
    return () => clearTimeout(timer)
  }, [catalog, channelId, date, options, requestGaps])

  const toggleRotation = useCallback((key: 'openerProgramIds' | 'closerProgramIds', programId: string) => {
    setOptions(prev => ({
      ...prev,
      [key]: prev[key].includes(programId)
        ? prev[key].filter(id => id !== programId)
        : [...prev[key], programId],
    }))
  }, [])
  const toggleOpener = useCallback((programId: string) => toggleRotation('openerProgramIds', programId), [toggleRotation])
  const toggleCloser = useCallback((programId: string) => toggleRotation('closerProgramIds', programId), [toggleRotation])

  const allSpotIds = useMemo(
    () => (catalog?.campaigns ?? []).flatMap(campaign => campaign.spots.map(spot => spot.id)),
    [catalog?.campaigns],
  )
  const selectedSpotIds = options.spotIds ?? allSpotIds

  const toggleCampaign = useCallback((campaignId: string) => {
    setOptions(prev => {
      const campaign = catalog?.campaigns.find(item => item.id === campaignId)
      if (!campaign) return prev
      const current = prev.spotIds ?? allSpotIds
      const ids = campaign.spots.map(spot => spot.id)
      const active = ids.every(id => current.includes(id))
      const next = active
        ? current.filter(id => !ids.includes(id))
        : [...current, ...ids.filter(id => !current.includes(id))]
      return { ...prev, spotIds: next.length === allSpotIds.length ? null : next }
    })
  }, [allSpotIds, catalog?.campaigns])

  const setOption = useCallback(<K extends keyof AutoGapFillOptions>(key: K, value: AutoGapFillOptions[K]) => {
    setOptions(prev => ({ ...prev, [key]: value }))
  }, [])

  const gridIds = useMemo(() => [...new Set(requestGaps.map(gap => gap.gridId))], [requestGaps])
  const canApply = Boolean(channelId && plan && plan.plannedCount > 0 && !saving && !planning)

  const handleApply = useCallback(async () => {
    if (!canApply || !channelId) return
    setSaving(true)
    setError('')
    try {
      const result = await recordGridChange({
        label:   'comblement de tous les trous',
        gridIds,
        date,
      }, () => playoutApi.applyAutoGapFill(channelId, { date, gaps: requestGaps, options }))

      // La fenêtre reste ouverte sur le compte rendu : c'est là que le retrait
      // de la passe est le plus facile à atteindre, juste après le geste.
      setApplied(result)
      setSaving(false)
      onApplied()
    } catch (exception: any) {
      setError(exception?.message ?? 'Échec du comblage')
      setSaving(false)
    }
  }, [canApply, channelId, date, gridIds, onApplied, options, requestGaps])

  const handleRevert = useCallback(async () => {
    if (!channelId || !applied?.runId) return
    setReverting(true)
    setError('')
    try {
      const result = await playoutApi.revertAutoGapFillRun(channelId, applied.runId)
      const kept = result.keptCount > 0
        ? ` ${result.keptCount} élément${result.keptCount > 1 ? 's' : ''} déjà à l’antenne conservé${result.keptCount > 1 ? 's' : ''}.`
        : ''
      onDone(`Comblage retiré : ${result.removedCount} élément${result.removedCount > 1 ? 's' : ''} supprimé${result.removedCount > 1 ? 's' : ''}.${kept}`)
    } catch (exception: any) {
      setError(exception?.message ?? 'Échec du retrait')
      setReverting(false)
    }
  }, [applied?.runId, channelId, onDone])

  const infoserviceMissing = plan?.infoserviceMissing || (options.useInfoservice && catalog !== null && !catalog.infoservice)

  return (
    <ModalShell
      className="agenda-editor auto-fill"
      closeDisabled={saving}
      dismissOnOverlay={false}
      onClose={onClose}
      size="wide"
      title="Combler tous les trous"
    >
      <div className="auto-fill__body">
        <header className="auto-fill__summary">
          <div className="auto-fill__summary-main">
            <span className="auto-fill__summary-eyebrow">Journée du {date}</span>
            <span className="auto-fill__summary-value">
              {requestGaps.length} trou{requestGaps.length > 1 ? 's' : ''} · {fmtSec(plan?.totalGapSec ?? 0)} d’antenne à couvrir
            </span>
          </div>
          <div className="auto-fill__summary-stats">
            {planning
              ? <span className="auto-fill__summary-pending"><Spinner /> Calcul du plan…</span>
              : plan && (
                <>
                  <span><strong>{plan.plannedCount}</strong> comblés</span>
                  <span><strong>{plan.spotCount}</strong> spots</span>
                  <span><strong>{fmtSec(plan.remainingSec)}</strong> laissés au bouche-trou</span>
                </>
              )}
          </div>
        </header>

        {loading && <div className="auto-fill__loading"><Spinner /> Chargement du catalogue…</div>}

        {applied && (
          <div className="auto-fill__report" role="status">
            <p className="auto-fill__report-headline">
              <Check aria-hidden="true" />
              {applied.filledCount} trou{applied.filledCount > 1 ? 's' : ''} comblé{applied.filledCount > 1 ? 's' : ''} ·{' '}
              {applied.createdCount} élément{applied.createdCount > 1 ? 's' : ''} posé{applied.createdCount > 1 ? 's' : ''}
              {applied.failedCount > 0 && ` · ${applied.failedCount} en échec`}
            </p>
            {applied.outcomes.filter(outcome => outcome.status === 'FAILED').map(outcome => (
              <p key={outcome.startTime} className="auto-fill__report-failure">
                <AlertTriangle aria-hidden="true" />
                {fmtClock(outcome.startTime)} — {outcome.message}
              </p>
            ))}
            <p className="auto-fill__report-hint">
              Le retrait reste possible plus tard depuis le bandeau de la grille, même après fermeture.
            </p>
          </div>
        )}

        {!applied && !loading && catalog && (
          <div className="auto-fill__layout">
            <section className="auto-fill__rule" aria-label="Règle de comblage">
              <RotationPicker
                label="Habillage d’ouverture"
                hint="Posé juste après le programme précédent, en alternance d’un trou au suivant."
                jingles={catalog.jingles}
                selected={options.openerProgramIds}
                onToggle={toggleOpener}
              />
              <RotationPicker
                label="Habillage de fermeture"
                hint="Posé juste avant le programme suivant. Sans place pour les deux, aucun des deux n’est posé."
                jingles={catalog.jingles}
                selected={options.closerProgramIds}
                onToggle={toggleCloser}
              />

              <fieldset className="auto-fill__field">
                <legend className="auto-fill__field-label">Infoservice</legend>
                <p className="auto-fill__field-hint">
                  {catalog.infoservice
                    ? `« ${catalog.infoservice.title} », ${fmtSec(catalog.infoservice.durationSec)}.`
                    : 'Aucun infoservice sélectionné pour cette chaîne.'}
                </p>
                <label className="auto-fill__toggle">
                  <input
                    type="checkbox"
                    checked={options.useInfoservice}
                    onChange={event => setOption('useInfoservice', event.target.checked)}
                  />
                  Combler le reste avec l’infoservice
                </label>
                <label className="auto-fill__inline-field">
                  À partir de (secondes)
                  <input
                    type="number"
                    className="tp-input"
                    min={1}
                    max={3600}
                    value={options.minInfoserviceSec}
                    disabled={!options.useInfoservice}
                    onChange={event => setOption('minInfoserviceSec', Math.max(1, Number(event.target.value) || 1))}
                  />
                </label>
                <label className="auto-fill__toggle">
                  <input
                    type="checkbox"
                    checked={options.infoserviceRepeat}
                    disabled={!options.useInfoservice}
                    onChange={event => setOption('infoserviceRepeat', event.target.checked)}
                  />
                  Enchaîner les passages jusqu’au bout du trou
                </label>
              </fieldset>

              <fieldset className="auto-fill__field">
                <legend className="auto-fill__field-label">Publicité</legend>
                <p className="auto-fill__field-hint">
                  Campagnes actives du jour. Les spots tournent d’un trou au suivant ; le moins diffusé passe en premier.
                  {options.useInfoservice && (
                    <> Un spot qui laisserait moins de {options.minInfoserviceSec} s derrière lui n’est pas posé :
                    la place revient entière à l’infoservice.</>
                  )}
                </p>
                <div className="auto-fill__rotation">
                  {catalog.campaigns.length === 0 && <p className="auto-fill__empty">Aucune campagne active ce jour</p>}
                  {catalog.campaigns.map(campaign => {
                    const ids = campaign.spots.map(spot => spot.id)
                    const active = ids.length > 0 && ids.every(id => selectedSpotIds.includes(id))
                    return (
                      <label key={campaign.id} className="auto-fill__option" data-selected={active || undefined}>
                        <input type="checkbox" checked={active} onChange={() => toggleCampaign(campaign.id)} />
                        <span className="auto-fill__option-title">
                          {campaign.name}
                          {campaign.advertiserName && <em> · {campaign.advertiserName}</em>}
                        </span>
                        <span className="auto-fill__option-meta">
                          {campaign.spots.length} spot{campaign.spots.length > 1 ? 's' : ''}
                          {campaign.maxDailyFrequency !== null && ` · ${campaign.placedToday}/${campaign.maxDailyFrequency} aujourd’hui`}
                        </span>
                      </label>
                    )
                  })}
                </div>
                <label className="auto-fill__toggle">
                  <input
                    type="checkbox"
                    checked={options.respectDailyCap}
                    onChange={event => setOption('respectDailyCap', event.target.checked)}
                  />
                  Respecter le plafond quotidien des campagnes
                </label>
                <label className="auto-fill__inline-field">
                  Spots maximum par trou
                  <input
                    type="number"
                    className="tp-input"
                    min={1}
                    max={50}
                    value={options.maxSpotsPerGap ?? ''}
                    placeholder="illimité"
                    onChange={event => setOption('maxSpotsPerGap', event.target.value === '' ? null : Number(event.target.value))}
                  />
                </label>
              </fieldset>

            </section>

            <section className="auto-fill__plan" aria-label="Plan de comblage">
              {infoserviceMissing && (
                <p className="auto-fill__warning" role="status">
                  <AlertTriangle aria-hidden="true" />
                  Aucun infoservice sélectionné : les reliquats resteront au bouche-trou du playout.
                </p>
              )}
              <ul className="auto-fill__plan-list">
                {(plan?.gaps ?? []).map(gap => <PlannedGapRow key={`${gap.gridId}:${gap.startTime}`} gap={gap} />)}
              </ul>
            </section>
          </div>
        )}

        {error && <p className="auto-fill__error" role="alert">{error}</p>}
      </div>

      <footer className="auto-fill__actions">
        {applied ? (
          <>
            <button
              type="button"
              className="tp-button tp-button--danger"
              onClick={handleRevert}
              disabled={reverting || !applied.runId}
            >
              {reverting ? <><Spinner /> Retrait…</> : <><Trash2 aria-hidden="true" /> Retirer ce comblage</>}
            </button>
            <button
              type="button"
              className="tp-button tp-button--primary"
              onClick={() => onDone('')}
              disabled={reverting}
            >
              Terminé
            </button>
          </>
        ) : (
          <>
            <button type="button" className="tp-button" onClick={onClose} disabled={saving}>Annuler</button>
            <button type="button" className="tp-button tp-button--primary" onClick={handleApply} disabled={!canApply}>
              {saving ? <><Spinner /> Comblage…</> : <><Sparkles aria-hidden="true" /> Combler {plan?.plannedCount ?? 0} trou{(plan?.plannedCount ?? 0) > 1 ? 's' : ''}</>}
            </button>
          </>
        )}
      </footer>
    </ModalShell>
  )
})
